/*
 * Copyright (c) 2024, King Living
 * Google Maps Component - GM-004
 */

import React, {useEffect, useRef, useState, useCallback, useMemo} from 'react';
import PropTypes from 'prop-types';
import {FormattedMessage} from 'react-intl';
import {Box, Spinner, Text, Alert, AlertIcon} from '@chakra-ui/react';

/**
 * GoogleMap Component
 *
 * @override GM-004
 * - Loads Maps JavaScript API using official NPM js-api-loader package
 * - Accepts apiKey, center, zoom, and markers as props
 * - Renders map in referenced DOM node
 * - Provides default center for each country
 */
const GoogleMap = ({
  apiKey,
  center,
  zoom = 10,
  markers = [],
  onMapLoad,
  onMapErrorChange,
  onMarkerClick,
  onMapClick,
  className,
  style,
  loading = 'Loading map...',
  error = 'Failed to load map',
  height = '400px',
  fitBoundsToMarkers = false,
  fitBoundsPadding = 50,
  focusBounds = null,
  focusMaxZoom = 14,
  mapTypeControl = true,
  ...props
}) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const googleMapsRef = useRef(null);
  const isInitializedRef = useRef(false);
  // KL-988 — true while a place-search viewport is framing the map, so the marker-bounds effect
  // below defers to it instead of zooming out to fit far-apart stores.
  const focusActiveRef = useRef(false);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);

  // Memoize initial center to prevent re-initialization
  const initialCenter = useMemo(() => {
    return center || {lat: -33.8688, lng: 151.2093}; // Default Sydney
  }, []); // Empty deps - only use initial value

  // Add markers to map
  const addMarkersToMap = useCallback((map, markerData, maps) => {
    if (!map || !maps || !markerData) return;

    // Clear existing markers
    markersRef.current.forEach((marker) => marker.setMap(null));
    markersRef.current = [];

    markerData.forEach((markerInfo, index) => {
      // Build marker icon if custom icon is provided
      let markerIcon = null;

      if (markerInfo.icon) {
        if (typeof markerInfo.icon === 'string') {
          // Simple URL string
          markerIcon = markerInfo.icon;
        } else if (markerInfo.icon.url) {
          // Custom icon object with url, scaledSize, anchor
          markerIcon = {
            url: markerInfo.icon.url,
            scaledSize: markerInfo.icon.scaledSize ?
              new maps.Size(markerInfo.icon.scaledSize.width, markerInfo.icon.scaledSize.height) :
              null,
            anchor: markerInfo.icon.anchor ?
              new maps.Point(markerInfo.icon.anchor.x, markerInfo.icon.anchor.y) :
              null,
          };
        }
      }

      const marker = new maps.Marker({
        position: markerInfo.position,
        map: map,
        title: markerInfo.title || `Store ${index + 1}`,
        icon: markerIcon,
        animation: markerInfo.animation || maps.Animation.DROP,
      });

      // Add click listener
      if (onMarkerClick) {
        marker.addListener('click', () => {
          onMarkerClick(markerInfo, marker);
        });
      }

      // Add info window if provided
      if (markerInfo.infoWindow) {
        const infoWindow = new maps.InfoWindow({
          content: markerInfo.infoWindow,
        });

        marker.addListener('click', () => {
          infoWindow.open(map, marker);
        });
      }

      markersRef.current.push(marker);
    });
  }, [onMarkerClick]);

  useEffect(() => {
    onMapErrorChange?.(!!errorMessage);
  }, [errorMessage, onMapErrorChange]);

  // Load Google Maps API only once on mount
  useEffect(() => {
    // Only initialize once
    if (isInitializedRef.current) return;

    let isMounted = true;
    let didInstallGmAuthFailure = false;
    let previousGmAuthFailure;

    const loadGoogleMapsAPI = async () => {
      if (!apiKey) {
        setErrorMessage('Google Maps API key is required');
        setIsLoading(false);

        return;
      }

      // Check if API key looks like a real key (starts with AIza)
      if (!apiKey.startsWith('AIza') || apiKey.length < 30) {
        setErrorMessage('Invalid Google Maps API key. Please check your configuration.');
        setIsLoading(false);

        return;
      }

      if (typeof window !== 'undefined') {
        previousGmAuthFailure = window.gm_authFailure;
        window.gm_authFailure = () => {
          if (!isMounted) return;
          setErrorMessage(
            'Google Maps authentication failed. Set a valid GOOGLE_MAPS_API_KEY in Business Manager and check API restrictions and billing.'
          );
          setIsLoading(false);
          if (typeof previousGmAuthFailure === 'function') {
            previousGmAuthFailure();
          }
        };
        didInstallGmAuthFailure = true;
      }

      try {
        // Dynamic import of Google Maps Loader
        const {Loader} = await import('@googlemaps/js-api-loader');

        const loader = new Loader({
          apiKey,
          version: 'weekly',
          libraries: ['places', 'geometry'],
        });

        const google = await loader.load();

        googleMapsRef.current = google.maps;

        // Initialize map
        if (mapRef.current && google.maps) {
          const map = new google.maps.Map(mapRef.current, {
            center: initialCenter,
            zoom: zoom,
            mapTypeId: google.maps.MapTypeId.ROADMAP,
            mapTypeControl: mapTypeControl,
            streetViewControl: true,
            fullscreenControl: true,
            zoomControl: true,
          });

          mapInstanceRef.current = map;
          isInitializedRef.current = true;

          // Add initial markers
          if (markers && markers.length > 0) {
            addMarkersToMap(map, markers, google.maps);
          }

          // Set up event listeners
          if (onMapLoad) {
            onMapLoad(map);
          }

          if (onMapClick) {
            map.addListener('click', (event) => {
              onMapClick(event.latLng);
            });
          }

          setIsLoading(false);
        }
      } catch (err) {
        console.error('Failed to load Google Maps:', err);
        if (didInstallGmAuthFailure && typeof window !== 'undefined') {
          window.gm_authFailure = previousGmAuthFailure;
          didInstallGmAuthFailure = false;
        }

        setErrorMessage(err?.message || error);
        setIsLoading(false);
      }
    };

    loadGoogleMapsAPI();

    return () => {
      isMounted = false;
      if (didInstallGmAuthFailure && typeof window !== 'undefined') {
        window.gm_authFailure = previousGmAuthFailure;
      }
    };
  }, [apiKey]); // Only depend on apiKey - load once

  // Update map center when center prop changes (without reloading)
  useEffect(() => {
    if (mapInstanceRef.current && center) {
      mapInstanceRef.current.panTo({lat: center.lat, lng: center.lng});
    }
  }, [center?.lat, center?.lng]);

  // Update zoom when zoom prop changes
  useEffect(() => {
    if (mapInstanceRef.current && zoom !== undefined) {
      const currentZoom = mapInstanceRef.current.getZoom();

      if (currentZoom !== zoom) {
        mapInstanceRef.current.setZoom(zoom);
      }
    }
  }, [zoom]);

  // KL-988 — `focusBounds` frames the nearest cluster of showrooms for the current search (computed
  // in use-google-maps-integration from the store distances). Fitting to this cluster — rather than
  // ALL returned markers (which span the whole country because the store API uses a huge radius) —
  // keeps the map zoomed to the searched city/suburb/state's local showrooms.
  const isFocusBoundsValid = Boolean(focusBounds) &&
    ['north', 'south', 'east', 'west'].every((key) => typeof focusBounds[key] === 'number');

  // KL-988 — fit to the showroom cluster. Takes priority over the marker-bounds effect below. A
  // `focusMaxZoom` clamp (applied once the map settles) prevents over-zoom when the cluster is a
  // single store or a very tight group, which fitBounds would otherwise push to street level.
  useEffect(() => {
    focusActiveRef.current = isFocusBoundsValid;

    const map = mapInstanceRef.current;
    const maps = googleMapsRef.current;

    if (!map || !maps || !isFocusBoundsValid) return undefined;

    map.fitBounds(
      {
        north: focusBounds.north,
        south: focusBounds.south,
        east: focusBounds.east,
        west: focusBounds.west,
      },
      fitBoundsPadding
    );

    const listener = maps.event.addListenerOnce(map, 'idle', () => {
      if (map.getZoom() > focusMaxZoom) {
        map.setZoom(focusMaxZoom);
      }
    });

    return () => {
      maps.event.removeListener(listener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusBounds?.north, focusBounds?.south, focusBounds?.east, focusBounds?.west, isFocusBoundsValid, fitBoundsPadding, focusMaxZoom]);

  // Update markers when markers prop changes
  useEffect(() => {
    if (mapInstanceRef.current && googleMapsRef.current && markers) {
      addMarkersToMap(mapInstanceRef.current, markers, googleMapsRef.current);

      // Fit bounds to show all markers if enabled — unless a searched-place viewport is currently
      // framing the map (KL-988), in which case we keep that zoom-to-place framing.
      if (fitBoundsToMarkers && markers.length > 0 && !focusActiveRef.current) {
        const bounds = new googleMapsRef.current.LatLngBounds();

        markers.forEach((marker) => {
          if (marker.position) {
            bounds.extend(marker.position);
          }
        });

        // Only fit bounds if we have valid bounds
        if (!bounds.isEmpty()) {
          // If only one marker, center on it with appropriate zoom
          if (markers.length === 1) {
            mapInstanceRef.current.setCenter(markers[0].position);
            mapInstanceRef.current.setZoom(14);
          } else {
            // Multiple markers - fit to bounds with padding
            mapInstanceRef.current.fitBounds(bounds, fitBoundsPadding);
          }
        }
      }
    }
  }, [markers, addMarkersToMap, fitBoundsToMarkers, fitBoundsPadding]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      markersRef.current.forEach((marker) => marker.setMap(null));
      markersRef.current = [];
    };
  }, []);

  if (errorMessage) {
    return (
      <Box
        alignItems="center"
        bg="gray.50"
        borderRadius="lg"
        display="flex"
        flexDirection="column"
        height={height}
        justifyContent="center"
        p={6}
        textAlign="center"
        width="100%"
        {...props}
      >
        <Alert borderRadius="md" maxWidth="400px" mb={4} status="warning">
          <AlertIcon />
          <Box>
            <Text fontWeight="bold" mb={2}>
              <FormattedMessage
                defaultMessage="Google Maps Not Available"
                id="google_map.error.not_available"
              />
            </Text>
            <Text fontSize="sm">{errorMessage}</Text>
            <Text color="gray.600" fontSize="xs" mt={2}>
              <FormattedMessage
                defaultMessage="To enable Google Maps, please:"
                id="google_map.error.enable_instructions"
              />
              <br />1. <FormattedMessage
                defaultMessage="Get a valid API key from Google Cloud Console"
                id="google_map.error.step_1"
              />
              <br />2. <FormattedMessage
                defaultMessage="Update GOOGLE_MAPS_API_KEY in your environment variables"
                id="google_map.error.step_2"
              />
              <br />3. <FormattedMessage
                defaultMessage="Restart the development server"
                id="google_map.error.step_3"
              />
            </Text>
          </Box>
        </Alert>
      </Box>
    );
  }

  return (
    <Box height={height} position="relative" width="100%" {...props}>
      {isLoading && (
        <Box
          alignItems="center"
          bg="white"
          bottom="0"
          display="flex"
          justifyContent="center"
          left="0"
          position="absolute"
          right="0"
          top="0"
          zIndex="1"
        >
          <Spinner color="blue.500" size="lg" />
          <Text ml={3}>{loading}</Text>
        </Box>
      )}

      <Box
        ref={mapRef}
        className={className}
        height="100%"
        style={style}
        width="100%"
      />
    </Box>
  );
};

GoogleMap.propTypes = {
  apiKey: PropTypes.string.isRequired,
  center: PropTypes.shape({
    lat: PropTypes.number.isRequired,
    lng: PropTypes.number.isRequired,
  }),
  className: PropTypes.string,
  error: PropTypes.string,
  fitBoundsPadding: PropTypes.number,
  fitBoundsToMarkers: PropTypes.bool,
  focusBounds: PropTypes.shape({
    east: PropTypes.number,
    north: PropTypes.number,
    south: PropTypes.number,
    west: PropTypes.number,
  }),
  focusMaxZoom: PropTypes.number,
  height: PropTypes.string,
  loading: PropTypes.string,
  markers: PropTypes.arrayOf(
    PropTypes.shape({
      animation: PropTypes.string,
      icon: PropTypes.string,
      infoWindow: PropTypes.string,
      position: PropTypes.shape({
        lat: PropTypes.number.isRequired,
        lng: PropTypes.number.isRequired,
      }).isRequired,
      title: PropTypes.string,
    })
  ),
  mapTypeControl: PropTypes.bool,
  onMapClick: PropTypes.func,
  onMapErrorChange: PropTypes.func,
  onMapLoad: PropTypes.func,
  onMarkerClick: PropTypes.func,
  style: PropTypes.object,
  zoom: PropTypes.number,
};

GoogleMap.displayName = 'GoogleMap';

export default GoogleMap;
